import {
  EMERGENCY_EQUIPMENT_CONTACT_STATES,
  StateAbbreviation,
} from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ContactReportRole, TankOwnerType } from "generated-graphql/graphql";

export const getEPCRAContactRoles = (
  ...facilityStates: Array<string | null | undefined>
): ContactReportRole[] => {
  const epcraRoles = [
    ContactReportRole.EpcraTierIiContact,
    ContactReportRole.EpcraEmergencyContact,
    ContactReportRole.EpcraEmergencyCoordinator,
    ContactReportRole.EpcraOwnerOperator,
    ContactReportRole.EpcraBilling,
    ContactReportRole.EpcraChemicalCarrier,
    ContactReportRole.EpcraT2MDirectSiteCommunication,
    ContactReportRole.EpcraT2MParentCompany,
    ContactReportRole.EpcraRegionalEnvironmentalContact,
  ];

  if (
    facilityStates.length &&
    facilityStates.every((state) =>
      EMERGENCY_EQUIPMENT_CONTACT_STATES.includes(state as StateAbbreviation)
    )
  ) {
    epcraRoles.push(ContactReportRole.EpcraEmergencyEquipmentContact);
  }

  if (
    facilityStates.length &&
    facilityStates.every((state) => state === "CA")
  ) {
    const caRoles = [
      ContactReportRole.EpcraCaOperator,
      ContactReportRole.EpcraCaOwner,
      ContactReportRole.EpcraCaPropertyOwner,
      ContactReportRole.EpcraCaTankOperator,
      ContactReportRole.EpcraCaTankOwner,
    ];
    return epcraRoles.concat(caRoles);
  }

  return epcraRoles;
};

// You can find these documented here: https://encamp.notion.site/RCRA-Roles-2572f9edc08f8062ae74d3881923d081
export const getRcraContactRoles = (
  ...facilityStates: Array<string | null | undefined>
): ContactReportRole[] => {
  const rcraRoles = [
    ContactReportRole.RcraOwner,
    ContactReportRole.RcraOperator,
    ContactReportRole.RcraSiteContact,
    ContactReportRole.RcraAuthorizedRepresentative,
    ContactReportRole.RcraSiteManager,
  ];

  if (
    [
      "AL",
      "AR",
      "AZ",
      "CA",
      "CO",
      "DE",
      "IA",
      "IL",
      "IN",
      "KS",
      "KY",
      "LA",
      "MI",
      "MN",
      "MO",
      "MS",
      "MT",
      "NJ",
      "NM",
      "NY",
      "OK",
      "OR",
      "RI",
      "SC",
      "VT",
      "WA",
      "WI",
      "WV",
    ].some((state) => facilityStates.includes(state))
  ) {
    rcraRoles.push(ContactReportRole.RcraBillingContact);
  }

  if (["KY", "LA"].some((state) => facilityStates.includes(state))) {
    rcraRoles.push(ContactReportRole.RcraRealPropertyOwner);
  }

  if (facilityStates.includes("FL")) {
    rcraRoles.push(ContactReportRole.RcraHurricanePointOfContact);
  }

  return rcraRoles;
};

export const contactRoleToLabel = (role: ContactReportRole) => {
  switch (role) {
    case ContactReportRole.EpcraCaOperator:
      return "Operator";
    case ContactReportRole.EpcraCaOwner:
      return "Owner";
    case ContactReportRole.EpcraCaPropertyOwner:
      return "Property Owner";
    case ContactReportRole.EpcraCaTankOperator:
      return "Tank Operator";
    case ContactReportRole.EpcraCaTankOwner:
      return "Tank Owner";
    case ContactReportRole.EpcraOwnerOperator:
      return "Owner / Operator";
    case ContactReportRole.EpcraEmergencyContact:
      return "Emergency Contact";
    case ContactReportRole.EpcraEmergencyCoordinator:
      return "Emergency Coordinator";
    case ContactReportRole.EpcraTierIiContact:
      return "Tier II Contact";
    case ContactReportRole.EpcraBilling:
      return "Billing Contact";
    case ContactReportRole.EpcraChemicalCarrier:
      return "Chemical Carrier";
    case ContactReportRole.EpcraT2MDirectSiteCommunication:
      return "Direct Site Communication";
    case ContactReportRole.EpcraT2MParentCompany:
      return "Parent Company";
    case ContactReportRole.EpcraEmergencyEquipmentContact:
      return "Emergency Equipment Contact";
    case ContactReportRole.EpcraRegionalEnvironmentalContact:
      return "Regional Environmental Contact";
    case ContactReportRole.RcraAuthorizedRepresentative:
      return "RCRA Authorized Representative";
    case ContactReportRole.RcraBillingContact:
      return "RCRA Billing Contact";
    case ContactReportRole.RcraHurricanePointOfContact:
      return "RCRA Hurricane Point of Contact";
    case ContactReportRole.RcraOwner:
      return "RCRA Owner";
    case ContactReportRole.RcraOperator:
      return "RCRA Operator";
    case ContactReportRole.RcraRealPropertyOwner:
      return "RCRA Real Property Owner";
    case ContactReportRole.RcraSiteContact:
      return "RCRA Site Contact";
    case ContactReportRole.RcraSiteManager:
      return "RCRA Site Manager";
    default:
      return prettyPrintEnumValue(role);
  }
};

export const getTankOwnerTypeOptions = () => {
  return [
    { display: "None", value: null },
    {
      display: "Local Agency/District",
      value: TankOwnerType.LocalAgency,
    },
    {
      display: "County Agency",
      value: TankOwnerType.CountyAgency,
    },
    {
      display: "State Agency",
      value: TankOwnerType.StateAgency,
    },
    {
      display: "Federal Agency",
      value: TankOwnerType.FederalAgency,
    },
    {
      display: "Non-Government",
      value: TankOwnerType.NonGovernment,
    },
  ];
};
